import { FC, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from '@kizen/kds/Icon';
import { Typography } from '@kizen/kds/Typography';
import { DASHBOARD } from 'queries/query-keys';
import TemporaryApiKeyService from 'services/TemporaryApiKeyService';
import { usePiChat } from 'ts-components/PiChat/usePiChat';
import type { ChatItem } from 'ts-components/PiChat/reducer';
import {
  selectCanRedo,
  selectCanSend,
  selectCanUndo,
  selectCurrentFiles,
  selectIsBusy,
  selectIsRestoring,
  selectRedoTarget,
  selectUndoTarget,
  useBlockVersions,
} from 'ts-components/AiBlockPanel/blockVersions';
import { DEFAULT_KICODE_URL } from 'ts-components/PiChat/connectionDefaults';
import {
  resolveConnectOptions,
  type AiBlockPanelContext,
} from './connectOptions';
import { FileTree } from './FileTreeSection';
import { PiChatPanel } from 'ts-components/PiChat/PiChatPanel';
import {
  selectChatPhase,
  useLoadingReveal,
} from 'ts-components/PiChat/PiChatSkeleton';
import { Button } from '@kizen/kds/Button';
import { INITIAL_RESTORE_EVENTS, restoreEventsReducer } from './restoreEvents';
import { handleBlockCapture } from './blockCapture';
import { fetchVersionFiles, versionFilesUrls } from './versionFiles';
import { BlockToolRow, RestoreEventRow } from './BlockToolRow';
import { CopyPromptMenu } from './CopyPromptMenu';
import { captureDashletForRequest } from './captureDashlet';
import { createTemporaryCredentials } from 'ts-components/PiChat/temporaryCredentials';
import { credentialsErrorText } from 'ts-components/PiChat/errorCopy';
import { StatusText } from 'ts-components/PiChat/styles';

export type { AiBlockPanelContext };

export type AiBlockPanelProps = {
  context: AiBlockPanelContext;
  dashletId?: string;
  conversationId?: string;
  onClose: () => void;
  onBlockPushed?: (dashletId: string, conversationId: string) => void;
  blockName?: string | null;
  dashboardName?: string | null;
  objectName?: string;
  className?: string;
};

export const AiBlockPanel: FC<AiBlockPanelProps> = ({
  context,
  dashletId,
  conversationId,
  onClose,
  onBlockPushed,
  blockName,
  dashboardName,
  objectName,
  className,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const reportedBlockRef = useRef({
    dashletId: dashletId ?? null,
    conversationId: conversationId ?? null,
  });
  const itemsRef = useRef<readonly ChatItem[]>([]);
  const [versions, dispatchVersions, versionsRef] = useBlockVersions();
  const [captureInFlight] = useState(() => new WeakSet<AbortSignal>());
  const [{ events: restoreEvents }, dispatchRestoreEvent] = useReducer(
    restoreEventsReducer,
    INITIAL_RESTORE_EVENTS
  );

  const dashboardQueryKey = [...DASHBOARD.DASHBOARD, context.dashboardId];
  const refreshRef = useRef<Promise<unknown>>(Promise.resolve());
  const refreshDashboard = () => {
    const refresh = queryClient.invalidateQueries({
      queryKey: dashboardQueryKey,
    });
    refreshRef.current = refresh;
    return refresh;
  };

  const [credentials] = useState(() =>
    createTemporaryCredentials({
      fetchKey: () => TemporaryApiKeyService.createTemporaryApiKey(),
    })
  );

  const socket = usePiChat({
    getCredentials: credentials.get,
    credentialsErrorNotice: credentialsErrorText(t),
    isSendBlocked: () => versionsRef.current.restoringSnapshotId !== null,
    onServerMessage: (message, meta) => {
      const before = dispatchVersions({ type: 'server', message });
      for (const { snapshotId, filesUrl } of versionFilesUrls(message)) {
        if (versionsRef.current.filesBySnapshotId[snapshotId]) continue;
        fetchVersionFiles(filesUrl, meta.url, meta.signal).then(
          (files) => {
            if (meta.signal.aborted) return;
            dispatchVersions({ type: 'filesLoaded', snapshotId, files });
          },
          (error) => {
            if (meta.signal.aborted) return;
            console.warn('AI block version files could not be loaded', error);
          }
        );
      }
      if (message.type === 'session.ready') void refreshDashboard();
      if (message.type === 'push.result') {
        void refreshDashboard();
        const reported = reportedBlockRef.current;
        if (
          message.dashletId !== reported.dashletId ||
          message.conversationId !== reported.conversationId
        ) {
          reportedBlockRef.current = {
            dashletId: message.dashletId,
            conversationId: message.conversationId,
          };
          onBlockPushed?.(message.dashletId, message.conversationId);
        }
      }
      if (message.type === 'snapshot.restored') {
        void refreshDashboard();
        dispatchRestoreEvent({
          type: 'restored',
          snapshotIds: before.snapshots.map((snapshot) => snapshot.id),
          previousPointer: before.pointer,
          restoredSnapshotId: message.snapshotId,
          afterCount: itemsRef.current.length,
        });
        if (
          !before.snapshots.some(
            (snapshot) => snapshot.id === message.snapshotId
          )
        ) {
          addNotice({ code: 'unknown_snapshot_restored' });
        }
      }
      if (message.type === 'block.capture') {
        handleBlockCapture(
          message,
          meta,
          (request, { signal, reply }) => {
            void captureDashletForRequest(request.dashletId, {
              signal,
              reply,
              waitForRefresh: () => refreshRef.current,
            });
          },
          captureInFlight
        );
      }
    },
  });
  const {
    connectionState,
    items,
    connect,
    addNotice,
    restoreSnapshot: sendRestore,
  } = socket;
  itemsRef.current = items;
  const panelState = { ...socket, ...versions };

  const resolveStart = () =>
    resolveConnectOptions({
      url: DEFAULT_KICODE_URL,
      context,
      latest: {
        dashletId: versionsRef.current.dashletId,
        conversationId: versionsRef.current.conversationId,
      },
      initial: { dashletId, conversationId },
    });
  const handleConnect = () => {
    dispatchRestoreEvent({ type: 'connecting' });
    connect(resolveStart);
    dispatchVersions({ type: 'connecting' });
  };
  const handleConnectRef = useRef(handleConnect);
  handleConnectRef.current = handleConnect;

  useEffect(() => {
    handleConnectRef.current();
  }, []);

  const restoreSnapshot = async (snapshotId: string) => {
    if (versionsRef.current.restoringSnapshotId !== null) return;
    dispatchVersions({ type: 'restoreRequested', snapshotId });
    if (!(await sendRestore(snapshotId))) {
      dispatchVersions({ type: 'restoreAborted' });
    }
  };

  const handleUndo = () => {
    const target = selectUndoTarget(versions);
    if (target) void restoreSnapshot(target.id);
  };

  const handleRedo = () => {
    const target = selectRedoTarget(versions);
    if (target) void restoreSnapshot(target.id);
  };

  const chatPhase =
    socket.reconnecting || connectionState === 'idle'
      ? 'ready'
      : selectChatPhase(connectionState);
  const isLoading = chatPhase === 'loading';
  const loadingRevealed = useLoadingReveal(isLoading);

  const currentFiles = selectCurrentFiles(versions);
  const canUndo = selectCanUndo(panelState);
  const canRedo = selectCanRedo(panelState);
  const isRestoring = selectIsRestoring(panelState);
  const versionCount = versions.snapshots.length;

  return (
    <PiChatPanel
      className={className}
      chat={socket}
      phase={chatPhase}
      loadingRevealed={loadingRevealed}
      canSend={selectCanSend(panelState)}
      busy={selectIsBusy(panelState)}
      onClose={onClose}
      title={blockName ?? t('New block')}
      label={blockName || t('AI panel')}
      placeholder={t('Describe the block')}
      emptyPrompt={t('Describe the block you want to build')}
      status={
        isRestoring ? (
          <StatusText data-qa-id="ai-block-panel-restoring">
            <Icon icon="action-loading" size="sm" color="font-placeholder" />
            <Typography size="sm" color="font-placeholder">
              {t('Restoring…')}
            </Typography>
          </StatusText>
        ) : socket.reconnecting ? (
          <StatusText data-qa-id="ai-block-panel-reconnecting">
            <Icon icon="action-loading" size="sm" color="font-placeholder" />
            <Typography size="sm" color="font-placeholder">
              {t('Reconnecting…')}
            </Typography>
          </StatusText>
        ) : isLoading ? (
          <StatusText>
            <Icon icon="action-loading" size="sm" color="font-placeholder" />
            <Typography size="sm" color="font-placeholder">
              {t('Connecting…')}
            </Typography>
          </StatusText>
        ) : versionCount > 1 ? (
          <StatusText data-qa-id="ai-block-panel-version">
            <Typography size="sm" color="font-placeholder">
              {t('Version {{current}} of {{total}}', {
                current: versions.pointer + 1,
                total: versionCount,
              })}
            </Typography>
          </StatusText>
        ) : null
      }
      actions={
        <>
          <Button
            variant="text"
            color="tertiary"
            leftIcon="text-undo"
            leftIconSettings={{
              size: 'sm',
              tooltipTitleOverride: '',
              tooltipDescriptionOverride: t('Undo'),
              tooltipPosition: 'top',
            }}
            onClick={handleUndo}
            disabled={!canUndo}
            qa={{ id: 'ai-block-panel-undo' }}
          />
          <Button
            variant="text"
            color="tertiary"
            leftIcon="text-redo"
            leftIconSettings={{
              size: 'sm',
              tooltipTitleOverride: '',
              tooltipDescriptionOverride: t('Redo'),
              tooltipPosition: 'top',
            }}
            onClick={handleRedo}
            disabled={!canRedo}
            qa={{ id: 'ai-block-panel-redo' }}
          />
          {versions.dashletId ?? dashletId ? null : (
            <CopyPromptMenu
              target={{
                surface: context.targetType,
                dashboardId: context.dashboardId,
                dashboardName: dashboardName ?? '',
                objectId: context.objectId,
                objectName,
              }}
            />
          )}
        </>
      }
      onReconnect={handleConnect}
      aboveLog={
        <FileTree
          files={currentFiles}
          isLoading={isLoading}
          loadingRevealed={loadingRevealed}
        />
      }
      events={restoreEvents}
      renderEvent={(event) => <RestoreEventRow event={event} />}
      renderToolRow={(item) => (
        <BlockToolRow item={item} wsUrl={DEFAULT_KICODE_URL} />
      )}
    />
  );
};
